import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  quality?: number;
  loading?: 'lazy' | 'eager';
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
  objectFit?: 'cover' | 'contain' | 'fill';
  placeholder?: string;
  fill?: boolean; // Container'a göre tam boyut (Next.js fill benzeri)
  sizes?: string; // Responsive image sizes (örn: "(max-width: 768px) 100vw, 50vw")
}

const ImageContainer = styled.div<{ $width?: number; $height?: number; $fill?: boolean }>`
  position: relative;
  width: ${props => {
    if (props.$fill) return '100%';
    return props.$width ? `${props.$width}px` : '100%';
  }};
  height: ${props => {
    if (props.$fill) return '100%';
    return props.$height ? `${props.$height}px` : 'auto';
  }};
  overflow: hidden;
  background: #f0f0f0;
`;

const StyledImage = styled.img<{ 
  $isLoaded: boolean; 
  $objectFit?: string;
  $fill?: boolean;
}>`
  ${props => props.$fill ? `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  ` : `
    width: 100%;
    height: 100%;
  `}
  object-fit: ${props => props.$objectFit || 'cover'};
  opacity: ${props => props.$isLoaded ? 1 : 0};
  transition: opacity 0.3s ease-in-out;
  display: block;
`;

const Placeholder = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${props => props.$isVisible ? 'shimmer 1.5s infinite' : 'none'};
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: opacity 0.3s ease-in-out;

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }
`;

/**
 * Optimized Image Component for React
 * Similar to Next.js Image but works with Create React App
 * 
 * Features:
 * - Lazy loading with Intersection Observer
 * - Placeholder while loading
 * - Automatic size optimization hints
 * - Error handling
 */
const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  quality = 75,
  loading = 'lazy',
  className,
  onLoad,
  onError,
  objectFit = 'cover',
  placeholder,
  fill = false,
  sizes,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [shouldLoad, setShouldLoad] = useState(loading === 'eager');
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Lazy loading with Intersection Observer
  useEffect(() => {
    if (loading === 'eager' || shouldLoad) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before image enters viewport
        threshold: 0.01,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [loading, shouldLoad]);

  // React SPA'da server-side optimization yok
  // Resimler S3'ten direkt gelir, bu yüzden yükleme sırasında optimize edilmeli
  // Bu component sadece lazy loading ve placeholder sağlar
  const getOptimizedUrl = (url: string): string => {
    if (!url) return '';
    
    // Data URLs, placeholders için direkt dön
    if (url.startsWith('data:') || url.startsWith('/placeholder')) {
      return url;
    }
    
    // S3'ten direkt URL döndür
    // Not: Resimler yükleme sırasında (Admin panelinde) optimize edilmeli
    // Yeni yüklenen resimler zaten WebP ve küçük olacak
    return url;
  };

  const handleLoad = () => {
    setIsLoaded(true);
    if (onLoad) onLoad();
  };

  const handleError = () => {
    setHasError(true);
    if (onError) onError();
  };

  if (hasError) {
    return (
      <ImageContainer 
        ref={containerRef} 
        $width={width} 
        $height={height}
        $fill={fill}
        className={className}
      >
        <Placeholder $isVisible={true}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            color: '#999'
          }}>
            Resim yüklenemedi
          </div>
        </Placeholder>
      </ImageContainer>
    );
  }

  return (
    <ImageContainer 
      ref={containerRef} 
      $width={width} 
      $height={height}
      $fill={fill}
      className={className}
    >
      <Placeholder $isVisible={!isLoaded} />
      {shouldLoad && (
        <StyledImage
          ref={imgRef}
          src={getOptimizedUrl(src)}
          alt={alt}
          $isLoaded={isLoaded}
          $objectFit={objectFit}
          $fill={fill}
          sizes={sizes}
          onLoad={handleLoad}
          onError={handleError}
          loading={loading}
          decoding="async"
        />
      )}
    </ImageContainer>
  );
};

export default OptimizedImage;

